import { toast, toastTypeEnum } from 'common/utils';
import { observable, action, makeObservable } from 'mobx';
import {
  createUserService,
  disableUserService,
  editUserService,
  getAllUsers,
  getUserProfile,
  IAddUserApiData,
  IdisableUserApiData,
  IEditUserApiData,
  IgetUserCreditHistoryParams,
  getUserCreditHistoryService,
  getUserCreatedService,
  IgetUserCreatedParams,
  IGetUserPrintingHistoryParams,
  getUserPrintingHistoryService,
  getUserLoginActivityService
} from 'services/users';
import { getApiErrorMessage } from 'services/utils';
// import { loginService } from 'services/users';
import { RootStore } from 'stores';
import {
  IgetUserApiData,
  IPrintRecord,
  ITransactionInfo,
  ILoginActivity,
  IUser,
  IUserInfo,
  pagination,
  sortByParams
} from 'types/index';

export type IInitialState = {
  data: {
    users: IUser[];
    total: number | null;
    getUsersParams: sortByParams & pagination;
    userDetail: {
      user_details?: IUserInfo;
      login_activity_total_count?: number;
      login_activity?: ILoginActivity[];
      credit_total_count?: number;
      credit_history?: ITransactionInfo[];
      user_created?: IUser[];
      user_total_count?: number;
      printHistory?: IPrintRecord[];
      print_total_count?: number;
    };
    userLoginActivityParams: sortByParams & pagination;
    userDetailParams: sortByParams & pagination;
    userCreatedParams: sortByParams & pagination;
    printHistoryParams: sortByParams & pagination;
  };
  ui: {
    isUsersLoading: boolean;
    disableUserLoading: null | string;
    editUserLoading: null | string;
    addUserLoading: boolean;
    getUserCreditHistory: boolean;
    getCreatedBy: boolean;
    getLoginActivity: boolean;
    getPrintHistory: boolean;
  };
};

export const initialState: IInitialState = {
  data: {
    users: [],
    total: null,
    getUsersParams: { limit: 10, offset: 0 },
    userDetail: {},
    userDetailParams: { limit: 10, offset: 0 },
    userCreatedParams: { limit: 10, offset: 0 },
    printHistoryParams: { limit: 10, offset: 0 },
    userLoginActivityParams: { limit: 10, offset: 0 }
  },
  ui: {
    isUsersLoading: false,
    disableUserLoading: null,
    editUserLoading: null,
    addUserLoading: false,
    getUserCreditHistory: false,
    getLoginActivity: false,
    getCreatedBy: false,
    getPrintHistory: false
  }
};

export class UsersStore {
  state: IInitialState = initialState;
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      state: observable,
      getUsers: action,
      disableUser: action,
      editUser: action,
      addUser: action,
      getUserCreditHistory: action,
      getUserLoginActivity: action,
      getCreatedByUsers: action,
      getUsersPrintHistory: action,
      setPrintHistoryParams: action,
      removeUserDetails: action,
      setState: action,
      reset: action
    });

    this.rootStore = rootStore;
    /* use this space to hydrate state from localStorage */
  }

  getUsers = async (params: IgetUserApiData, showLoading = true) => {
    try {
      showLoading &&
        this.setState({
          ui: { ...this.state.ui, isUsersLoading: true }
        });

      if (!params?.search || params?.search.length === 0) {
        delete params.search;
      }
      const { data } = await getAllUsers({ ...params });

      this.setState({
        data: {
          ...this.state.data,
          users: data.users.rows,
          total: data.users.count
        }
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.state.ui.isUsersLoading = false;
    }
  };

  disableUser = async (params: IdisableUserApiData) => {
    try {
      this.setState({
        ui: { ...this.state.ui, disableUserLoading: params.id }
      });
      await disableUserService(params);
      await this.getUsers(this.state.data.getUsersParams, false);
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({
        ui: { ...this.state.ui, disableUserLoading: null }
      });
    }
  };

  editUser = async (params: IEditUserApiData) => {
    try {
      const {
        AuthStore: {
          state: {
            data: { user },
            data
          },
          setState
        }
      } = this.rootStore;
      this.setState({
        ui: { ...this.state.ui, editUserLoading: params.id }
      });

      await editUserService(params);
      const foundUser = this.state.data.users.find(u => u.id === params.id);
      if (foundUser && user && user.userType === 'distributor') {
        const {
          data: {
            data: { credit }
          }
        } = await getUserProfile(user.id);
        setState({
          data: {
            ...data,
            user: { ...user, credit }
          }
        });
      }
      await this.getUsers(this.state.data.getUsersParams, false);
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({
        ui: { ...this.state.ui, editUserLoading: null }
      });
    }
  };

  addUser = async (params: IAddUserApiData): Promise<boolean | undefined> => {
    try {
      const {
        AuthStore: {
          state: {
            data: { user },
            data
          },
          setState
        }
      } = this.rootStore;
      this.setState({
        ui: { ...this.state.ui, addUserLoading: true }
      });
      await createUserService(params);

      if (user) {
        const {
          data: {
            data: { credit }
          }
        } = await getUserProfile(user.id);
        setState({
          data: {
            ...data,
            user: { ...user, credit }
          }
        });
      }
      await this.getUsers(this.state.data.getUsersParams, false);
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({
        ui: { ...this.state.ui, addUserLoading: false }
      });
    }
  };

  getUserCreditHistory = async (params: IgetUserCreditHistoryParams) => {
    try {
      this.setState({ ui: { ...this.state.ui, getUserCreditHistory: true } });
      const { data } = await getUserCreditHistoryService(params);
      this.setState({
        data: {
          ...this.state.data,
          userDetail: {
            ...this.state.data.userDetail,
            user_details: data.data.user_details,
            credit_history: data.data.credit_history,
            credit_total_count: data.data.total_count
          }
        }
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getUserCreditHistory: false } });
    }
  };

  getUserLoginActivity = async (params: IgetUserCreditHistoryParams) => {
    try {
      this.setState({ ui: { ...this.state.ui, getLoginActivity: true } });
      const { data } = await getUserLoginActivityService(params);
      this.setState({
        data: {
          ...this.state.data,
          userDetail: {
            ...this.state.data.userDetail,
            login_activity: data.data.rows,
            login_activity_total_count: data.data.count
          }
        }
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getLoginActivity: false } });
    }
  };

  getCreatedByUsers = async (params: IgetUserCreatedParams) => {
    try {
      this.setState({ ui: { ...this.state.ui, getCreatedBy: true } });
      const { data } = await getUserCreatedService(params);

      this.setState({
        data: {
          ...this.state.data,
          userDetail: {
            ...this.state.data.userDetail,
            user_created: data.data.rows,
            user_total_count: data.data.count
          }
        }
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getCreatedBy: false } });
    }
  };

  getUsersPrintHistory = async (params: IGetUserPrintingHistoryParams) => {
    try {
      this.setState({ ui: { ...this.state.ui, getPrintHistory: true } });
      const { data } = await getUserPrintingHistoryService(params);
      this.setState({
        data: {
          ...this.state.data,
          userDetail: {
            ...this.state.data.userDetail,
            printHistory: data.data.prints,
            print_total_count: data.data.total
          }
        }
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getPrintHistory: false } });
    }
  };

  setPrintHistoryParams = (p: pagination & sortByParams) => {
    this.setState({
      data: {
        ...this.state.data,
        printHistoryParams: {
          ...this.state.data.printHistoryParams,
          ...p
        }
      }
    });
  };

  removeUserDetails = () => {
    this.setState({ data: { ...this.state.data, userDetail: {} } });
  };

  setState = (params: Partial<IInitialState>) => {
    const { state } = this;
    this.state = {
      ...state,
      ...params
    };
    /* use this space to save/sync state with localStorage */
  };

  reset = () => {
    this.state = initialState;
    /* use this space to reset localStorage in case if you're persisting this state */
  };
}
